import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Checkbox,
  TextField,
  Button,
  Divider,
  Stack,
} from "@mui/material";
import { subscribeDepositPlanRequest } from "../../redux/DepositePlan/depositeSlice";
import { useDispatch, useSelector } from "react-redux";
import GppGoodIcon from "@mui/icons-material/GppGood";
import { toast } from "react-toastify";
import { useAppTheme } from "../../theme/useAppTheme";

export default function DepositScreen() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { plan: depositPlan, balance: availableBalance } = location.state || {};

  const { appConfig } = useSelector((state) => state.dashboard || {});
  const DECIMAL_VALUE = appConfig?.decimalPrecision ?? 4;

  // Receive plan and balance data from AllDepositPlanScreen
  const { plan, balance } = location.state || {};

  const themeColors = useAppTheme();

  const [grams, setGrams] = useState("");
  const [autoRenew, setAutoRenew] = useState(false);
  const [loading, setLoading] = useState(false);

  const isDataMissing = !depositPlan || !availableBalance;

  useEffect(() => {
    if (isDataMissing) {
      toast.error("Deposit plan not found. Please select a plan again.", {
        toastId: "deposit-plan-missing",
      });
      navigate("/deposit-plans", { replace: true }); // use plan list route
    }
  }, [isDataMissing, navigate]);

  if (isDataMissing) return null;

  const metalBalance = Number(
    Number(availableBalance.availableBalance ?? 0).toFixed(DECIMAL_VALUE),
  );

  const benefit = (
    (
      ((depositPlan.qty * depositPlan.interest) / 100) / 365) *
    depositPlan.duration
  ).toFixed(4);

  const handleConfirm = () => {
    if (!grams || grams === "0") {
      toast.warn("Enter valid grams");
      return;
    }
    if (+grams > metalBalance) {
      toast.warn("Quantity exceeds available balance");
      return;
    }
    if (+grams < depositPlan.qty) {
      toast.warn(`Minimum deposit is ${depositPlan.qty} gm(s)`);
      return;
    }

    setLoading(true);

    dispatch(
      subscribeDepositPlanRequest({
        planid: depositPlan._id,
        bullionid: depositPlan.bullion._id,
        metalName: depositPlan.bullion.metalName,
        weight: Number(grams),
        autorenew: autoRenew,
        startDate: Date.now(),
      }),
    );

    setTimeout(() => {
      setLoading(false);
      navigate(-1);
    }, 1500);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        py: 4,
        background: themeColors.scaffoldGradientColors,
      }}
    >
      <Card
        sx={{
          maxWidth: 1000,
          width: "100%",
          borderRadius: 3,
          boxShadow: 8,
          background: themeColors.bigContainer,
          color: themeColors.text,
          border: `1px solid ${themeColors.scaffold}`,
        }}
      >
        <Box sx={{ px: { xs: 3, sm: 5 }, py: 4 }}>
          {/* Page Title */}
          <Typography
            variant="h4"
            fontWeight="bold"
            align="center"
            sx={{
              mb: 3,
              fontSize: { xs: "1.8rem", sm: "2.2rem" },
              background: themeColors.primary,
              backgroundClip: "text",
              textFillColor: "transparent",
            }}
          >
            {depositPlan.bullion.metalName} Deposit
          </Typography>

          {/* Plan Name */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              mb: 3,
              textAlign: "center",
              color: themeColors.text,
            }}
          >
            {depositPlan.name}
          </Typography>

          {/* Metal Gradient */}
          <Box
            sx={{
              background:
                plan.bullion.metalName === "Gold"
                  ? "linear-gradient(135deg, #FFD700, #DAA520)"
                  : plan.bullion.metalName === "Silver"
                    ? "linear-gradient(135deg, #C0C0C0, #A9A9A9)"
                    : "linear-gradient(135deg, #E5E4E2, #D3D3D3)",
              borderRadius: "20px",
              p: 2,
              mb: 3,
              color: "#000",
              textAlign: "center",
              border: `1px solid ${themeColors.scaffold}`,
            }}
          >
            <Typography variant="h5" fontWeight={600}>
              {depositPlan.bullion.metalName}-{depositPlan.bullion.karat}k-
              {depositPlan.bullion.purity}
            </Typography>
          </Box>

          {/* Duration & Min Qty */}
          <Stack direction="row" justifyContent="space-between" mb={2}>
            <Box>
              <Typography variant="body2">Duration</Typography>
              <Typography variant="h6">
                {depositPlan.duration}{" "}
                <Typography component="span" variant="body2">
                  {depositPlan.unit.toLowerCase()}
                </Typography>
              </Typography>
            </Box>

            <Box textAlign="right">
              <Typography variant="body2">Min Qty</Typography>
              <Typography variant="h6">
                {depositPlan.qty}{" "}
                <Typography component="span" variant="body2">
                  gm(s)
                </Typography>
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ background: themeColors.text, my: 2 }} />

          {/* Benefit */}
          <Typography variant="body2" align="center" sx={{ mb: 3 }}>
            Upon completion of this plan's duration, you will receive a benefit
            of{" "}
            <Typography component="span" fontWeight={900}>
              {benefit} gm(s)
            </Typography>{" "}
            benefit for every {depositPlan.qty} gm(s) deposited.
          </Typography>

          {/* Current Balance */}
          <Box
            sx={{
              background: themeColors.mediumContainer,
              color: themeColors.text,
              border: `1px solid ${themeColors.scaffold}`,
              borderRadius: "15px",
              p: 2,
              mb: 3,
              textAlign: "center",
            }}
          >
            <Typography variant="h6">Current Balance</Typography>
            <Typography variant="h6" fontWeight={600}>
              {metalBalance} gm
            </Typography>
          </Box>

          {/* Input */}
          <TextField
            fullWidth
            type="number"
            label="Enter grams"
            value={grams}
            onChange={(e) => setGrams(e.target.value)}
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                background: themeColors.mediumContainer,
                color: themeColors.text,
              },
              "& .MuiInputLabel-root": {
                color: themeColors.text,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: themeColors.scaffold,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: themeColors.primary,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: themeColors.primary,
              },
            }}
          />
          {/* Safety Note */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.8,
              mt: -2,
              mb: 3,
              color: themeColors.text,
            }}
          >
            <GppGoodIcon sx={{ fontSize: 16, color: "#4CAF50" }} />
            <Typography variant="caption">
              100% Safe & Secured | Pure {depositPlan.bullion.metalName}
            </Typography>
          </Box>

          {/* Auto Renew */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              background: themeColors.mediumContainer,
              color: themeColors.text,
              border: `1px solid ${themeColors.scaffold}`,
              borderRadius: "15px",
              p: 2,
              mb: 3,
            }}
          >
            <Checkbox
              checked={autoRenew}
              onChange={(e) => setAutoRenew(e.target.checked)}
              sx={{ color: "#ffffff" }}
            />
            <Box>
              <Typography fontWeight={500}>Auto Renew</Typography>
              <Typography variant="body2">
                Enabling this will auto renew your plan at the end of its
                duration
              </Typography>
            </Box>
          </Box>

          {/* Confirm Button */}
          <Button
            fullWidth
            variant="contained"
            disabled={loading}
            onClick={handleConfirm}
            sx={{
              background: themeColors.primary,
              color: themeColors.text,
              "&:hover": {
                background: themeColors.svgColor,
              },
              border: `1px solid ${themeColors.scaffold}`,
              fontWeight: 500,
              fontSize: { xs: "1rem", sm: "1.1rem" },
              px: 5,
              py: 1.2,
              textTransform: "none",
              borderRadius: 2,
            }}
          >
            {loading ? "Processing..." : "Confirm"}
          </Button>
        </Box>
      </Card>
    </Box>
  );
}
